import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

const CHOSHIM_DEMO_ALIASES = ["choshim", "cho_shim"];
const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface ChoshimPattern {
  number: number;
  name: string;
  meaning: string;
  joonbi: string;
  steps: string[];
}

interface ChoshimProps {
  selectedBelt: string;
  getBeltColor: (belt: string) => string;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
}

function ChoshimPatternBody({ pattern }: { pattern: ChoshimPattern }) {
  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-4">
      <div className="space-y-4">
        <div className="mb-4 rounded-lg bg-white p-3 shadow-sm">
          <h4 className="mb-2 text-base font-medium text-taekwondo-red">Key:</h4>
          <ul className="space-y-1 text-gray-700">
            <li>CS - Cat stance</li>
            <li>BS - Back stance</li>
            <li>FS - Front stance</li>
            <li>TS - Tiger stance</li>
            <li>KH - Knife Hand</li>
          </ul>
        </div>

        {pattern.number === 1 && (
          <div className="rounded-lg bg-white p-4 shadow-sm">
            <h4 className="mb-3 text-base font-medium text-taekwondo-red">JOONBI</h4>
            <div className="space-y-2 text-gray-700">
              <p className="font-medium">Starting Position:</p>
              <p>{pattern.joonbi}</p>
            </div>
          </div>
        )}

        <div className="rounded-lg bg-white p-4 shadow-sm">
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Moves</h4>
          <ol className="list-none space-y-2">
            {pattern.steps.map((step, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span className="text-gray-700">
                  {step.includes("KIHAP") ? (
                    <>
                      {step.replace(" KIHAP", "")}{" "}
                      <span className="font-semibold">KIHAP</span>
                    </>
                  ) : (
                    step
                  )}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

export default function Choshim({
  selectedBelt,
  getBeltColor: _getBeltColor,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
  hasDemo,
  onWatchDemo,
}: ChoshimProps) {
  if (
    !["NAVY", "BLACK/WHITE", "BLACK/D1", "BLACK/D2", "BLACK/D3", "BLACK/D4", "BLACK/D5"].includes(
      selectedBelt,
    )
  ) {
    return null;
  }

  let numPatterns = 0;
  if (selectedBelt === "NAVY") {
    numPatterns = 5;
  } else if (selectedBelt === "BLACK/WHITE") {
    numPatterns = 8;
  } else if (["BLACK/D1", "BLACK/D2", "BLACK/D3", "BLACK/D4", "BLACK/D5"].includes(selectedBelt)) {
    numPatterns = 9;
  }

  const defaultJoonbi =
    "Diamond hands groin raise up, hands round to R fist in L palm, Bring feet together at end";
  const pattern1Joonbi =
    "Diamond hands groin raise up, hands round to R fist in L palm, Bring feet together at end";

  const patternSteps = {
    1: "Cover across body with right hand stepping right leg into Horse stance left middle punch KIHAP, Right middle punch KIHAP, 2 x high punches (L/R) KIHAP on 2nd, Triple punch (2 mid (L/R), 1 L high KIHAP), Repeat triple punches 2 mid R/L, 1 R high KIHAP",
    2: "L 90° BS down block, Adjust into FS reverse mid punch, Step up together R parry down, Step L FS, L Mid punch, Turn 180° repeat",
    3: "R leg backwards (face front) BS rising block, Adjust FS high punch, Step R to L high R, KH Block, Step L forward high L punch, 180° Turn repeat",
    4: "R Leg back slide front back into CS, Inner forearm block, Double mid-section kempo punch over, Adjust into FS L arm cover/Pressing block with R arm mid kempo fist over, 180° Turn repeat",
    5: "R leg back BS L KH block, Adjust FS reverse KH strike R hand, Step through with R downward parry & L mid punch, Adjust to TS 45* high punch/left downward parry, Step through and turn 180° Repeat",
    6: "R leg back KH guarding block, Fake L front kick land L parry down & reverse mid punch, Rear leg fake front kick R hand down parry/ L punch L leg backward 180° Repeat",
    7: "R leg back L inner hammer BS, Reverse punch, Jump skip change into KH guard block, With a cut to R side, Spin knife hand strike, Reverse punch with face open hand cover, R leg cross step over L with L KH block, L leg jump fwd with superman punch (& open hand face cover) and land in X stance with L KH block, L leg unwind 360* & step back into BS, Repeat",
    8: "R leg backwards 90* BS downward KH guarding block, L Uppercut (45* straight punch to jaw) with KH high cover to R side TS, Adj back to CS with R punch L downward parry, Step back R arm pushing block adj FS punch over, L leg 180° back, Repeat",
    9: "R leg back into eagle/crane arms (triangle) in CS (hands lower than chest), Wind rear arm in, Stamp R leg to left (behind L) with L hand in mantis hand block to body, Land in FS with rear arm ridge hand to neck with left hand under elbow, Lead leg sweep with L arm sweeping backwards (scissor sweep), Spin and end in groin palm heel (rear arm) and lead arm high KH block next to ear, Pull back into crossing downward KH guarding blk, Step L leg back 180* (back end round), Repeat, Crane arms and drop back knee then stand to Habeul Jah seh (Joonbi)",
  };

  const patterns: ChoshimPattern[] = Array.from({ length: numPatterns }, (_, i) => {
    const number = i + 1;
    const joonbi = number === 1 ? pattern1Joonbi : defaultJoonbi;
    const steps = patternSteps[number as keyof typeof patternSteps]
      .split(",")
      .map((step) => step.trim());
    return {
      number,
      name: `Choshim ${number}`,
      meaning: `Beginner's Mind ${number}`,
      joonbi,
      steps,
    };
  });

  const fingerprint = patterns.map((p) => p.number).join("|");
  const [activeTab, setActiveTab] = useState(() => String(patterns[0]?.number ?? 1));

  useEffect(() => {
    setActiveTab(String(patterns[0]?.number ?? 1));
  }, [fingerprint]);

  const surface = beltTabSurfaceClass;

  if (presentation === "curriculum") {
    return (
      <div className="space-y-4 text-left">
        <h2 className="text-xl font-semibold text-taekwondo-red md:text-2xl">
          Choshim - Beginners mind
        </h2>
        {hasDemo?.(CHOSHIM_DEMO_ALIASES) && (
          <div>
            <button
              type="button"
              className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
              onClick={() => onWatchDemo?.(CHOSHIM_DEMO_ALIASES)}
            >
              Watch Demonstration Video
            </button>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-4">
          <ScrollableCurriculumTabStrip key={fingerprint} ariaLabelPrefix="Choshim patterns">
            {patterns.map((p) => (
              <TabsTrigger
                key={p.number}
                value={String(p.number)}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-4 py-2 text-sm font-medium tabular-nums transition-shadow",
                  surface,
                  "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
                )}
              >
                {p.number}
              </TabsTrigger>
            ))}
          </ScrollableCurriculumTabStrip>

          {patterns.map((p) => (
            <TabsContent
              key={p.number}
              value={String(p.number)}
              className="mt-0 focus-visible:outline-none"
            >
              <ChoshimPatternBody pattern={p} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    );
  }

  return (
    <Accordion type="single" collapsible className="w-full space-y-2">
      {patterns.map((pattern) => (
        <AccordionItem
          key={`choshim-pattern-${pattern.number}`}
          value={`choshim-pattern-${pattern.number}`}
        >
          <AccordionTrigger className="rounded-lg bg-gray-50 px-4 py-2 hover:bg-gray-100 hover:no-underline">
            <div className="flex w-full items-center text-left">
              <span className="flex-1">
                {pattern.name} ({pattern.meaning})
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-0">
            <ChoshimPatternBody pattern={pattern} />
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
